'use client';

import { useEffect } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { Login, LoginVideoPanel } from '@/app/components/shared/Login';
import { Dialog, DialogContent, DialogPortal } from '@/app/components/ui/dialog';
import { useStore } from '@/app/providers/store';
import type { UserInfo } from '@/app/types';

export interface StreamLoginDialogProps {
  open: boolean;
  onClose?: (userInfo?: UserInfo) => void;
}

export const StreamLoginDialog = ({ open, onClose }: StreamLoginDialogProps) => {
  const userInfo = useStore((state) => state.userInfo);

  useEffect(() => {
    if (open && userInfo) onClose?.(userInfo);
  }, [onClose, open, userInfo]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose?.()}>
      <DialogPortal>
        <DialogContent
          className="w-fit max-w-[calc(100vw-32px)] border-none bg-transparent p-0 shadow-none sm:p-0 md:p-0"
          overlayClassname="bg-black/60"
          closable={false}
          variant="mobileFullScreen"
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <button
            type="button"
            className="absolute top-3 right-3 z-20 flex size-8 items-center justify-center rounded-full text-system-white hover:text-system-white80"
            onClick={() => onClose?.()}
            aria-label="Close login dialog"
          >
            <BaseLineClose />
          </button>
          <div className="flex gap-4 rounded-[28px] border border-[rgba(255,255,255,0.16)] bg-system-bg07 sm:rounded-none sm:border-none">
            <LoginVideoPanel className="w-[520px] shrink-0 rounded-none rounded-l-[27px] md:hidden lg:w-[440px]" />
            <div className="h-[720px] shrink-0 p-6 sm:h-auto sm:min-h-screen sm:w-full sm:p-4 sm:pb-[calc(var(--ios-safe-bottom)+16px)] lg:h-[608px]">
              <Login
                fillHeight
                skipPostLoginInitialization
                onLoginSuccess={onClose}
                className="border-none bg-transparent shadow-none backdrop-blur-none sm:w-full sm:rounded-none"
              />
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
